import { Avatar, Badge, ButtonBase, Divider, Menu, MenuItem, Skeleton, Typography } from '@mui/material';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { FaPen } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

import './EditableAvatar.css';
import { useAuth } from '../../context/AuthContext';
import { useProfile } from '../../context/ProfileContext';
import { compressAvatar, getAvatarUrl, getInitials } from '../../utils';
import apiClient from '../../services/apiService';
import { useToast } from '../../context/ToastContext';

const EditableAvatar = memo(() => {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const { user: { userImage, username }, updateUserImage, deleteUserImage } = useAuth() as any;
    const { state, loading } = useProfile();
    const [anchorElUserImage, setAnchorUserImage] = useState<HTMLElement | null>(null);
    const [avatarSrc, setAvatarSrc] = useState<string | null>(null);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const memoizedAvatarUrl = useMemo(() => {
        if (!userImage?.fileName) return null;
        return getAvatarUrl(userImage);
    }, [userImage]);

    useEffect(() => {
        setAvatarSrc(memoizedAvatarUrl);
    }, [memoizedAvatarUrl]);

    const title = (state.currentProfile.info as { title?: string })?.title;
    const initials = useMemo(() => getInitials(title, username), [title, username]);

    const handleOpenUserImageMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorUserImage(event.currentTarget);
    };

    const handleCloseUserImageMenu = () => {
        setAnchorUserImage(null);
    };

    const handleAddImage = () => {
        handleCloseUserImageMenu();
        if (fileInputRef.current) fileInputRef.current.value = '';
        fileInputRef.current?.click();
    };

    const handleButtonBaseClick = (e: React.MouseEvent<HTMLElement>) => {
        if (avatarSrc !== null) {
            e.preventDefault();
            handleOpenUserImageMenu(e);
        } else {
            if (fileInputRef.current) fileInputRef.current.value = '';
            fileInputRef.current?.click();
        }
    };

    const handleAvatarChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) {
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }
        try {
            setUploading(true);
            const compressed = await compressAvatar(file);

            const reader = new FileReader();
            reader.onload = () => setAvatarSrc(reader.result as string);
            reader.readAsDataURL(compressed.file);

            const formData = new FormData();
            formData.append('file', compressed.file);
            formData.append('mode', compressed.mode);

            const { data } = await apiClient.post('/user/avatar', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            if (data.status === 1) {
                const newFileName = data.imageUrl.split('/').pop();
                updateUserImage(newFileName, data.updatedAt);
                setAvatarSrc(`${data.imageUrl}?v=${new Date(data.updatedAt).getTime()}`);
                showToast('success', t('profile.preview.user-image.success'));
            }
        } catch (error: any) {
            console.error('Error uploading image:', error);

            if (error.message === 'too_big') {
                showToast('error', t('profile.preview.user-image.error.size'));
                return;
            }
            if (error.message === 'compress_failed') {
                showToast('error', t('profile.preview.user-image.error.compress'));
                return;
            }

            switch (error.response?.status) {
                case -1: showToast('error', t('profile.preview.user-image.error.size')); break;
                case -2: showToast('error', t('profile.preview.user-image.error.unsupported')); break;
                case -3: showToast('error', t('profile.preview.user-image.error.compress')); break;
                default: showToast('error', t('profile.preview.user-image.error.upload'));
            }
            setAvatarSrc(avatarSrc);
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteAvatar = async () => {
        try {
            setUploading(true);
            const { data } = await apiClient.delete('/user/avatar');
            if (data.status === 1) {
                deleteUserImage();
                setAvatarSrc(null);
                if (fileInputRef.current) fileInputRef.current.value = '';
                showToast('success', t('profile.preview.user-image.delete'));
                handleCloseUserImageMenu();
            }
        } catch (error) {
            showToast('error', t('profile.preview.user-image.error.delete'));
        } finally {
            setUploading(false);
        }
    };

    if (loading) {
        return (
            <div className="editable-user-image">
                <Skeleton variant="circular" className="editable-avatar-loading-skeleton" />
            </div>
        );
    }

    return (
        <div className="editable-user-image">
            <ButtonBase
                component="label"
                role={undefined}
                tabIndex={-1}
                aria-label={t('profile.info.avatar.aria')}
                disabled={uploading}
                onClick={handleButtonBaseClick}
                className="editable-user-image-button"
            >
                <Badge
                    overlap="circular"
                    className="user-image-badge"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    badgeContent={
                        <div className="user-image-badge-container">
                            <FaPen fontSize={11} />
                        </div>
                    }
                >
                    <Avatar className="editable-avatar" src={avatarSrc ?? undefined}>
                        {!avatarSrc && (
                            <Typography component="span" className="editable-avatar-initials">
                                {initials}
                            </Typography>
                        )}
                    </Avatar>
                    {uploading && (
                        <Skeleton
                            className="user-image-skeleton"
                            variant="circular"
                            width={150}
                            height={150}
                            sx={{ bgcolor: 'rgba(232, 231, 231, 0.3)' }}
                        />
                    )}
                </Badge>
            </ButtonBase>
            <input
                ref={fileInputRef}
                type="file"
                accept="image/heic,image/heif,image/jpeg,image/png,image/webp,image/*"
                className="editable-avatar-file-input"
                onChange={handleAvatarChange}
            />
            <Menu
                sx={{ mt: '5px' }}
                id="user-image"
                anchorEl={anchorElUserImage}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                keepMounted
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                open={Boolean(anchorElUserImage)}
                onClose={handleCloseUserImageMenu}
            >
                <MenuItem onClick={handleAddImage}>
                    <Typography variant="body1" color="primary">{t('profile.preview.user-image.change')}</Typography>
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleDeleteAvatar}>
                    <Typography variant="body1" color="primary">{t('profile.preview.user-image.remove')}</Typography>
                </MenuItem>
            </Menu>
        </div>
    );
});

export default EditableAvatar;
